import type { InventoryLevel } from '../interfaces/ILogistics.js';

/** Reorder, or the jump to the draft already raised. A draft carries its ref,
 *  so a caller that jumps to it never re-checks `draftOrderRef` for null. */
export type IReorderAction = { kind: 'order' } | { kind: 'draft'; draftRef: string };

/** The one action a tracked stock row offers. A covered row's reorder (past
 *  its reorder point) carries the lead time it was decided on. */
export type StockRowAction =
  | { kind: 'sync' | 'dueSync' | 'none' }
  | IReorderAction
  | (IReorderAction & { leadTimeDays: number });

export interface IStockRowActionOptions {
  /** The day's "did it arrive?" ask was already answered "not yet". */
  dueDismissed?: boolean;
}

/** Reorder, unless a draft already exists — then the jump to it (never a second draft). */
function reorderAction(level: InventoryLevel): IReorderAction {
  return level.draftOrderRef === null ? { kind: 'order' } : { kind: 'draft', draftRef: level.draftOrderRef };
}

/**
 * The inventory row's action chain, one action at most. Ran out: answer a due
 * ask, Sync a landed order, or reorder when nothing is on the way. Covered:
 * Sync a landed order, answer a due ask, or reorder past the reorder point.
 * 'none' means the row only reports (an in-flight order, or nothing to do).
 */
export function stockRowAction(level: InventoryLevel, { dueDismissed = false }: IStockRowActionOptions = {}): StockRowAction {
  const dueAsk = level.arrivalUnconfirmed && !dueDismissed;
  if (level.needsCheck) {
    if (dueAsk) return { kind: 'dueSync' };
    if (level.awaitingRenewal) return { kind: 'sync' };
    return level.deliveryIncoming ? { kind: 'none' } : reorderAction(level);
  }
  if (level.awaitingRenewal) return { kind: 'sync' };
  if (level.deliveryIncoming) return dueAsk ? { kind: 'dueSync' } : { kind: 'none' };
  if (level.overdue && level.leadTimeDays !== null) return { ...reorderAction(level), leadTimeDays: level.leadTimeDays };
  return { kind: 'none' };
}
